import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { supabase } from "../../lib/supabase";
import { getAdminReservations } from "../../services/adminReservationService";

const STATUS_OPTIONS = ["pending", "confirmed", "completed", "cancelled"];

const getStatusLabel = (status) => {
	const labels = {
		pending: "En attente",
		confirmed: "Confirmée",
		completed: "Terminée",
		cancelled: "Annulée",
	};
	return labels[status] || status || "En attente";
};

const getStatusBadgeClass = (status) => {
	const styles = {
		pending: "border-amber-200 bg-amber-50 text-amber-700",
		confirmed: "border-emerald-200 bg-emerald-50 text-emerald-700",
		completed: "border-teal-200 bg-teal-50 text-teal-700",
		cancelled: "border-rose-200 bg-rose-50 text-rose-700",
	};

	return styles[status] || "border-stone-200 bg-stone-50 text-stone-700";
};

const getRequestTypeLabel = (type) => {
	const labels = {
		simple: "Réservation simple",
		group: "Groupe",
		event: "Événement",
		professional: "Professionnelle",
		special: "Spéciale",
	};

	return labels[type] || type || "Réservation simple";
};

const formatDateTime = (date) => {
	if (!date) return "—";

	return new Intl.DateTimeFormat("fr-FR", {
		day: "2-digit",
		month: "long",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	}).format(new Date(date));
};

const formatReservationDate = (dateString) => {
	if (!dateString) return "—";

	const date = new Date(dateString);
	if (Number.isNaN(date.getTime())) return dateString;

	return new Intl.DateTimeFormat("fr-FR", {
		weekday: "short",
		day: "2-digit",
		month: "long",
		year: "numeric",
	}).format(date);
};

const normalizeText = (value) =>
	String(value || "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.trim();

const AdminReservationsPage = () => {
	const [reservations, setReservations] = useState([]);
	const [loading, setLoading] = useState(true);
	const [updatingStatusId, setUpdatingStatusId] = useState(null);
	const [statusFilter, setStatusFilter] = useState("all");
	const [searchTerm, setSearchTerm] = useState("");

	useEffect(() => {
		const loadReservations = async () => {
			try {
				const data = await getAdminReservations();
				setReservations(data || []);
			} catch (error) {
				console.error("Erreur chargement réservations :", error.message);
				toast.error("Erreur", {
					description: "Impossible de charger les réservations.",
				});
			} finally {
				setLoading(false);
			}
		};

		loadReservations();
	}, []);

	useEffect(() => {
		const channel = supabase
			.channel("admin-reservations-live")
			.on(
				"postgres_changes",
				{
					event: "INSERT",
					schema: "public",
					table: "reservations",
				},
				(payload) => {
					const newReservation = payload.new;

					setReservations((prev) => {
						const exists = prev.some(
							(reservation) => reservation.id === newReservation.id,
						);
						if (exists) return prev;
						return [newReservation, ...prev];
					});

					toast.success("Nouvelle réservation", {
						description: `${newReservation.first_name || ""} ${newReservation.last_name || ""} • ${newReservation.reservation_date || "Date inconnue"} à ${newReservation.reservation_time || "Heure inconnue"}`,
					});
				},
			)
			.on(
				"postgres_changes",
				{
					event: "UPDATE",
					schema: "public",
					table: "reservations",
				},
				(payload) => {
					const updatedReservation = payload.new;

					setReservations((prev) =>
						prev.map((reservation) =>
							reservation.id === updatedReservation.id
								? updatedReservation
								: reservation,
						),
					);
				},
			)
			.subscribe();

		return () => {
			supabase.removeChannel(channel);
		};
	}, []);

	useEffect(() => {
		const loadSession = async () => {
			const {
				data: { session },
				error: sessionError,
			} = await supabase.auth.getSession();

			console.log("SESSION ERROR:", sessionError);
			console.log("SESSION:", session);
			console.log("ACCESS TOKEN:", session?.access_token);
			console.log("USER:", session?.user);
		};

		loadSession();
	}, []);

	const handleUpdateReservationStatus = async (reservation, newStatus) => {
		if (!reservation?.id) return;

		const currentStatus = reservation.status || "pending";

		if (currentStatus === newStatus) {
			toast.info("Aucun changement", {
				description: `La réservation est déjà ${getStatusLabel(newStatus).toLowerCase()}.`,
			});
			return;
		}

		try {
			setUpdatingStatusId(reservation.id);

			const { data, error } = await supabase.functions.invoke(
				"update-reservation",
				{
					body: {
						reservation_id: reservation.id,
						status: newStatus,
					},
				},
			);

			if (error) {
				throw new Error(error.message || "Erreur lors de la mise à jour.");
			}

			if (!data?.success) {
				if (data?.code === "PENDING_NOT_ALLOWED") {
					toast.warning("Action non autorisée", {
						description: data.message || "Ce statut n’est plus modifiable.",
					});
					return;
				}

				throw new Error(data?.message || "La mise à jour a échoué.");
			}

			// update local state (cohérent avec realtime + UI optimiste)
			if (data?.reservation) {
				setReservations((prev) =>
					prev.map((item) =>
						item.id === reservation.id ? data.reservation : item,
					),
				);
			} else {
				setReservations((prev) =>
					prev.map((item) =>
						item.id === reservation.id ? { ...item, status: newStatus } : item,
					),
				);
			}

			toast.success("Réservation mise à jour", {
				description: `Statut : ${getStatusLabel(newStatus)}.`,
			});

			if (data?.emails?.client_error) {
				toast.warning("Email client non envoyé", {
					description: data.emails.client_error,
				});
			}

			if (data?.emails?.admin_error) {
				toast.warning("Email admin non envoyé", {
					description: data.emails.admin_error,
				});
			}
		} catch (error) {
			console.error("Erreur update-reservation :", error);

			toast.error("Erreur de mise à jour", {
				description: error.message || "Une erreur est survenue.",
			});
		} finally {
			setUpdatingStatusId(null);
		}
	};

	const filteredReservations = useMemo(() => {
		let data = reservations;

		if (statusFilter !== "all") {
			data = data.filter(
				(reservation) => (reservation.status || "pending") === statusFilter,
			);
		}

		const query = normalizeText(searchTerm);

		if (!query) return data;

		return data.filter((reservation) => {
			const fullName = normalizeText(
				`${reservation.first_name || ""} ${reservation.last_name || ""}`,
			);
			const email = normalizeText(reservation.email || "");

			return fullName.includes(query) || email.includes(query);
		});
	}, [reservations, statusFilter, searchTerm]);

	const stats = useMemo(() => {
		const counts = {
			total: reservations.length,
			pending: 0,
			confirmed: 0,
			completed: 0,
			cancelled: 0,
		};

		reservations.forEach((reservation) => {
			const status = reservation.status || "pending";
			if (counts[status] !== undefined) counts[status] += 1;
		});

		return counts;
	}, [reservations]);

	return (
		<div className="min-h-screen bg-[#f8f6f2]">
			<div className="mx-auto max-w-[1600px] px-4 pb-12 pt-28 sm:px-6 lg:px-8">
				<div className="mb-8 flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
					<div className="max-w-2xl">
						<p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#b08a60]">
							Administration
						</p>
						<h1 className="mt-3 text-3xl font-semibold tracking-tight text-[#2A2A2A] sm:text-4xl">
							Gestion des réservations
						</h1>
						<p className="mt-3 text-base leading-7 text-[#6f6a63] sm:text-lg">
							Suivi clair des demandes, mise à jour rapide des statuts et vue
							plus lisible pour l’équipe.
						</p>
					</div>

					<div className="w-full max-w-md xl:w-[380px]">
						<label
							htmlFor="reservation-search"
							className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-[#9a9288]"
						>
							Recherche rapide
						</label>
						<div className="relative">
							<input
								id="reservation-search"
								type="text"
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								placeholder="Nom complet ou email"
								className="h-12 w-full rounded-2xl border border-[#e7dfd3] bg-white pl-4 pr-11 text-sm text-[#2A2A2A] outline-none transition placeholder:text-[#a29b92] focus:border-[#b08a60] focus:ring-0"
							/>
							{searchTerm ? (
								<button
									type="button"
									onClick={() => setSearchTerm("")}
									className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full px-2 py-1 text-xs font-medium text-[#8e877d] hover:bg-[#f5f1ea]"
								>
									Effacer
								</button>
							) : (
								<span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-[#a29b92]">
									⌕
								</span>
							)}
						</div>
					</div>
				</div>

				<div className="mb-6 flex flex-wrap gap-3">
					<button
						type="button"
						onClick={() => setStatusFilter("all")}
						className={`rounded-full px-4 py-2.5 text-sm font-medium transition ${
							statusFilter === "all"
								? "bg-[#2f2925] text-white shadow-sm"
								: "border border-[#e7dfd3] bg-white text-[#5d5851] hover:bg-[#faf8f5]"
						}`}
					>
						Toutes ({stats.total})
					</button>
					<button
						type="button"
						onClick={() => setStatusFilter("pending")}
						className={`rounded-full px-4 py-2.5 text-sm font-medium transition ${
							statusFilter === "pending"
								? "bg-amber-500 text-white shadow-sm"
								: "border border-[#e7dfd3] bg-white text-[#5d5851] hover:bg-[#faf8f5]"
						}`}
					>
						En attente ({stats.pending})
					</button>
					<button
						type="button"
						onClick={() => setStatusFilter("confirmed")}
						className={`rounded-full px-4 py-2.5 text-sm font-medium transition ${
							statusFilter === "confirmed"
								? "bg-emerald-600 text-white shadow-sm"
								: "border border-[#e7dfd3] bg-white text-[#5d5851] hover:bg-[#faf8f5]"
						}`}
					>
						Confirmées ({stats.confirmed})
					</button>
					<button
						type="button"
						onClick={() => setStatusFilter("completed")}
						className={`rounded-full px-4 py-2.5 text-sm font-medium transition ${
							statusFilter === "completed"
								? "bg-teal-600 text-white shadow-sm"
								: "border border-[#e7dfd3] bg-white text-[#5d5851] hover:bg-[#faf8f5]"
						}`}
					>
						Terminées ({stats.completed})
					</button>
					<button
						type="button"
						onClick={() => setStatusFilter("cancelled")}
						className={`rounded-full px-4 py-2.5 text-sm font-medium transition ${
							statusFilter === "cancelled"
								? "bg-rose-600 text-white shadow-sm"
								: "border border-[#e7dfd3] bg-white text-[#5d5851] hover:bg-[#faf8f5]"
						}`}
					>
						Annulées ({stats.cancelled})
					</button>
				</div>

				<div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
					<div className="rounded-3xl border border-[#e8e0d5] bg-white p-5 shadow-[0_10px_30px_rgba(25,25,25,0.04)]">
						<p className="text-sm text-[#8b857d]">Total des réservations</p>
						<p className="mt-2 text-3xl font-semibold text-[#2A2A2A]">
							{stats.total}
						</p>
					</div>
					<div className="rounded-3xl border border-amber-100 bg-white p-5 shadow-[0_10px_30px_rgba(25,25,25,0.04)]">
						<p className="text-sm text-[#8b857d]">En attente</p>
						<p className="mt-2 text-3xl font-semibold text-amber-600">
							{stats.pending}
						</p>
					</div>
					<div className="rounded-3xl border border-emerald-100 bg-white p-5 shadow-[0_10px_30px_rgba(25,25,25,0.04)]">
						<p className="text-sm text-[#8b857d]">Confirmées</p>
						<p className="mt-2 text-3xl font-semibold text-emerald-600">
							{stats.confirmed}
						</p>
					</div>
					<div className="rounded-3xl border border-teal-100 bg-white p-5 shadow-[0_10px_30px_rgba(25,25,25,0.04)]">
						<p className="text-sm text-[#8b857d]">Terminées</p>
						<p className="mt-2 text-3xl font-semibold text-teal-600">
							{stats.completed}
						</p>
					</div>
				</div>

				<div className="overflow-hidden rounded-[32px] border border-[#e7dfd3] bg-white shadow-[0_16px_50px_rgba(28,25,23,0.05)]">
					<div className="hidden border-b border-[#eee7dd] bg-[#fcfaf7] px-8 py-5 xl:block">
						<div className="grid grid-cols-[1.4fr_1.35fr_0.9fr_0.7fr_0.55fr_0.9fr_0.85fr_1fr] gap-6">
							<p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8a857d]">
								Réservation
							</p>
							<p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8a857d]">
								Contact
							</p>
							<p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8a857d]">
								Date
							</p>
							<p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8a857d]">
								Heure
							</p>
							<p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8a857d]">
								Pers.
							</p>
							<p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8a857d]">
								Type
							</p>
							<p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8a857d]">
								Statut
							</p>
							<p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8a857d]">
								Action
							</p>
						</div>
					</div>

					{loading && (
						<div className="px-8 py-12 text-sm text-[#7A766F]">
							Chargement des réservations...
						</div>
					)}

					{!loading && filteredReservations.length === 0 && (
						<div className="px-8 py-12 text-sm text-[#7A766F]">
							{searchTerm
								? "Aucune réservation ne correspond à cette recherche."
								: "Aucune réservation pour le moment."}
						</div>
					)}

					{!loading &&
						filteredReservations.map((reservation, index) => {
							const currentStatus = reservation.status || "pending";
							const isUpdating = updatingStatusId === reservation.id;

							return (
								<div
									key={reservation.id}
									className={`px-6 py-6 sm:px-8 ${
										index !== filteredReservations.length - 1
											? "border-b border-[#f1ebe2]"
											: ""
									}`}
								>
									<div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.4fr_1.35fr_0.9fr_0.7fr_0.55fr_0.9fr_0.85fr_1fr] xl:items-start">
										<div>
											<p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#a29b92] xl:hidden">
												Réservation
											</p>
											<p className="mt-1 break-all text-lg font-semibold leading-8 text-[#2A2A2A]">
												#{reservation.id}
											</p>

											{reservation.message ? (
												<div className="mt-4 rounded-2xl bg-[#faf8f4] px-4 py-3">
													<p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#9a9288]">
														Message client
													</p>
													<p className="mt-2 text-sm leading-7 text-[#645f58]">
														{reservation.message}
													</p>
												</div>
											) : (
												<p className="mt-4 text-sm text-[#a29b92]">
													Aucun message client.
												</p>
											)}

											<p className="mt-4 text-xs text-[#9a9288]">
												Créée le {formatDateTime(reservation.created_at)}
											</p>
										</div>

										<div>
											<p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#a29b92] xl:hidden">
												Contact
											</p>
											<div className="rounded-2xl border border-[#efe8dd] bg-[#fffdfa] p-4">
												<p className="text-lg font-medium text-[#2A2A2A]">
													{[reservation.first_name, reservation.last_name]
														.filter(Boolean)
														.join(" ") || "—"}
												</p>
												<div className="mt-3 space-y-2">
													<p className="break-all text-sm text-[#5F5A54]">
														{reservation.email || "—"}
													</p>
													<p className="text-sm text-[#7A766F]">
														{reservation.phone || "—"}
													</p>
												</div>
											</div>
										</div>

										<div>
											<p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#a29b92] xl:hidden">
												Date
											</p>
											<p className="text-sm font-medium leading-7 text-[#2A2A2A]">
												{formatReservationDate(reservation.reservation_date)}
											</p>
										</div>

										<div>
											<p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#a29b92] xl:hidden">
												Heure
											</p>
											<p className="text-sm font-medium text-[#2A2A2A]">
												{reservation.reservation_time || "—"}
											</p>
										</div>

										<div>
											<p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#a29b92] xl:hidden">
												Pers.
											</p>
											<div className="inline-flex min-w-[56px] items-center justify-center rounded-2xl bg-[#f5f1ea] px-3 py-2 text-sm font-semibold text-[#2A2A2A]">
												{reservation.guest_count || "—"}
											</div>
										</div>

										<div>
											<p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#a29b92] xl:hidden">
												Type
											</p>
											<span className="inline-flex rounded-full border border-[#e7e0d4] bg-[#faf8f4] px-4 py-2 text-sm font-medium text-[#5F5A54]">
												{getRequestTypeLabel(reservation.request_type)}
											</span>
										</div>

										<div>
											<p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#a29b92] xl:hidden">
												Statut
											</p>
											<span
												className={`inline-flex rounded-full border px-4 py-2 text-sm font-semibold ${getStatusBadgeClass(
													currentStatus,
												)}`}
											>
												{getStatusLabel(currentStatus)}
											</span>
										</div>

										<div>
											<p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#a29b92] xl:hidden">
												Action
											</p>

											<div className="flex flex-col gap-3">
												<select
													value={currentStatus}
													disabled={isUpdating}
													onChange={(e) =>
														handleUpdateReservationStatus(
															reservation,
															e.target.value,
														)
													}
													className="h-12 rounded-2xl border border-[#e7dfd3] bg-white px-4 text-sm font-medium text-[#2A2A2A] outline-none transition focus:border-[#b08a60] disabled:cursor-not-allowed disabled:opacity-60"
												>
													{STATUS_OPTIONS.map((statusOption) => (
														<option key={statusOption} value={statusOption}>
															{getStatusLabel(statusOption)}
														</option>
													))}
												</select>

												{isUpdating ? (
													<p className="text-xs font-medium text-[#9a9288]">
														Mise à jour en cours...
													</p>
												) : (
													<p className="text-xs text-[#9a9288]">
														Le client reçoit automatiquement l’email lié au
														statut.
													</p>
												)}
											</div>
										</div>
									</div>
								</div>
							);
						})}
				</div>
			</div>
		</div>
	);
};

export default AdminReservationsPage;
